import { FC } from 'react';
import { FLOWS, MALLO_LEVELS, SuggerimentoMallo } from '@leopart/calc';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import { MENU_IN_PORTAL } from '../allenamenti.constants';
import { IBlockFormValues } from './useBlockForm';
import styles from './MalloFields.module.scss';

/** `''` fa da null nei select: react-select non gestisce value null. */
const NONE = '';

interface IMalloFieldsProps {
  values: IBlockFormValues;
  setField: <K extends keyof IBlockFormValues>(
    key: K,
    value: IBlockFormValues[K],
  ) => void;
  /** `null` quando non c'è niente da suggerire o i valori coincidono già. */
  suggestion: SuggerimentoMallo | null;
  onApplySuggestion: () => void;
}

const malloOptions = [
  { label: '— non classificato —', value: NONE },
  ...MALLO_LEVELS.map((l) => ({ label: l.labelIt, value: String(l.v) })),
];

const flowOptions = [
  { label: '— non classificato —', value: NONE },
  ...FLOWS.map((f) => ({
    label: `${f.labelIt} (×${f.fattore.toFixed(2)})`,
    value: f.id,
  })),
];

/**
 * Sezione «Classificazione carico — scala Mallo & flusso» del form blocco
 * (ADR 029): peso in % di gara + flusso, suggerimento automatico applicabile
 * con un click, spiegazione della scala in un espandibile chiuso di default.
 * Visibile solo se una categoria del blocco abilita il gruppo 'mallo'.
 */
const MalloFields: FC<IMalloFieldsProps> = ({
  values,
  setField,
  suggestion,
  onApplySuggestion,
}) => (
  <div className={styles.section}>
    <div className={styles.head}>
      Classificazione carico — scala Mallo &amp; flusso{' '}
      <span className={styles.tag}>Facoltativa</span>
    </div>

    <div className={styles.row}>
      <CustomSelectInput
        label="Peso Mallo (% gara)"
        options={malloOptions}
        value={values.malloWeight == null ? NONE : String(values.malloWeight)}
        onChange={(value) =>
          setField('malloWeight', value ? Number(value) : null)
        }
        {...MENU_IN_PORTAL}
      />
      <CustomSelectInput
        label="Flusso (IDC)"
        options={flowOptions}
        value={values.flowType ?? NONE}
        onChange={(value) =>
          setField(
            'flowType',
            (value || null) as IBlockFormValues['flowType'],
          )
        }
        {...MENU_IN_PORTAL}
      />
    </div>

    {suggestion && (
      <div className={styles.suggest}>
        <span>
          Suggerimento: <b>{suggestion.malloWeight}%</b> ·{' '}
          <b>
            {FLOWS.find((f) => f.id === suggestion.flowType)?.labelIt ??
              suggestion.flowType}
          </b>{' '}
          — {suggestion.reasonIt}
        </span>
        <CustomButton size="sm" color="outline" onClick={onApplySuggestion}>
          Applica
        </CustomButton>
      </div>
    )}

    <details className={styles.details}>
      <summary>Cos&apos;è la scala Mallo? A cosa servono questi due campi?</summary>
      <p>
        La scala di <b>Javier Mallo</b> (ex preparatore di Real Madrid e
        Manchester City) misura quanto un&apos;esercitazione con palla
        «assomiglia» alla partita, presa come 100%: più ci sono avversari veri,
        spazio e decisioni da prendere, più il peso sale.
      </p>
      <ul>
        <li><b>30%</b> — Tecnico senza opposizione: analitico, nessun avversario (ricezione orientata, dominio, rimessa tecnica)</li>
        <li><b>40%</b> — Opposizione ridotta: 1v1, 2v2, rondò, costruzione vs pressione, cross+marcatura, tiro in porta</li>
        <li><b>60%</b> — SSG in superiorità: jolly, 4v4+3, 1+4v6, possessi con jolly</li>
        <li><b>80%</b> — SSG in parità: 5v5/6v6 in spazio ridotto, partite a tema con vincoli</li>
        <li><b>100%</b> — Partita libera / gara: spazi ampi, senza vincoli</li>
      </ul>
      <p>
        Il <b>flusso</b> distingue come il carico viene erogato: due
        esercitazioni con lo stesso peso possono stressare in modo molto
        diverso. <b>Continuo</b> ×1.00 = FC e decisioni alte senza pause (SSG a
        flusso, partita, possessi); <b>Intermittente</b> ×0.65 = gioco a fasi
        alternate a organizzazione (1v1, 1+4&gt;6, cross+marcatura, costruzione
        organizzata); <b>Analitico</b> ×0.45 = FC bassa e recupero tra
        esecuzioni (tecnica isolata, rondò statico).
      </p>
      <p>
        <b>Cosa vedrai in Analisi</b> compilando questi campi: il{' '}
        <b>CE-tecnico</b> (quanto il lavoro con palla della settimana «vale» in
        % di gara), l&apos;<b>IDC</b> (lo stesso valore corretto per il flusso
        reale), il <b>carico cognitivo</b> (minuti con opposizione completa,
        ≥60%), il <b>carico muscolare</b> (minuti ≥80%, dove compaiono sprint e
        frenate da gara) e il <b>rapporto Continuo/Intermittente</b>, che
        avvisa se la squadra si sta allenando «a tratti» mentre la gara è a
        flusso continuo.
      </p>
      <p>
        Sono campi <b>facoltativi</b>: i blocchi senza classificazione restano
        validi e vengono solo esclusi da questi calcoli. Il lavoro atletico
        puro (forza, corsa, pliometria…) è già conteggiato nelle sue categorie
        e qui non va classificato.
      </p>
    </details>
  </div>
);

export default MalloFields;
